import { ChangeDetectorRef, Component, ElementRef, Input, ViewChild, SimpleChanges, OnInit, OnChanges, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import * as _ from 'lodash';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TabViewModule } from 'primeng/tabview';
import { forkJoin } from 'rxjs';
import { AppIcons } from '../../../../shared/app.icons';
import { AppSettings } from '../../../../shared/app.settings';
import { MITableComponent } from '../../../../shared/components/mi-table/mi-table.component';
import type { Country } from '../../../../shared/models/country';
import type { Language } from '../../../../shared/models/language';
import { CommonBindingDataService } from '../../../../shared/services/common-binding-data.service';
import { ConfigService } from '../../../../shared/services/config.service';
import type { EntityCountsResponse, EntityList } from '../../../../shared/models/entity.models';
import { PermissionAccessService } from 'app/modules/shared/services/permission-access.service';
import { LoaderService } from 'app/modules/shared/components/loader/loader.service';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { AddDeliveryAddressComponent } from "./add-delivery-address/add-delivery.component";
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { RestApiService } from 'app/modules/shared/services/rest-api.service';
import { ListResponse } from '../../../../shared/models/listResponse.models';
import type { AttributeData, Field } from '../../../../shared/models/attribute.models';

interface ColumnDefinition {
  attributeCode: string;
  attributeIndex: number;
  field: string;
  header: string;
  [key: string]: unknown;
}

interface FilterField {
  attributeId: number;
  attributeCode: string;
  attributeIndex: number;
  presetValues: Array<{
    labelKey: string;
    labelValue: string;
  }>;
  [key: string]: unknown;
}

@Component({
  selector: 'app-delivery-details',
  templateUrl: './delivery-details.component.html',
  styleUrl: './delivery-details.component.scss',
  standalone: true,
  imports: [TabViewModule, TranslateModule, MITableComponent, BreadcrumbModule,
    ButtonModule, DialogModule, AddDeliveryAddressComponent]
})
export class DeliveryDetailsComponent implements OnInit, OnChanges {
  @ViewChild('uploader', { static: true }) uploader: ElementRef<HTMLElement>;
  @ViewChild(MITableComponent) tableRef: MITableComponent;
  @Input() isActive: boolean = false;
  @Input() isSiteReady: boolean = false;
  @Input() siteEntityId: string;
  @Output() saveSuccess = new EventEmitter<void>();
  lastSortColumnDetails: any;

  miIcons = AppIcons;
  activeIndex: number = 0;
  isLoading = true;
  filterAvailableColumns: string[] = [];
  cols: ColumnDefinition[] = [];
  moduleName = AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS;
  filterList: FilterField[] = [];
  btnLabel: string;
  items: MenuItem[] | undefined;
  isShowMenu: boolean = true;
  attributeLabels: Record<string, string> = {};
  filterAttributeLabels: Record<string, string> = {};
  listColumns: ColumnDefinition[] = [];
  allColumns: ColumnDefinition[] = [];
  pagination: { limit: number; offset: number; searchStr: string; defaultSortColumn?: string; defaultSortType?: string } = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: ""
  };
  totalRecords: number = 0;
  tableData: Record<string, unknown>[] = [];
  buttons: Array<{ label: string; icon: string; command: () => void }> = [];
  country: Country;
  language: Language;
  entityCount: EntityCountsResponse;
  itemsPerPage: number;
  entityData: EntityList = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    defaultSortType: "desc",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: "",
    actionStatus: ''
  };
  routePath: MenuItem[] = [];
  deleteSiteMsg: string;
  deleteSiteHeaderMsg: string;
  showMenu: boolean = false;
  moduleHeaderSecondaryBtnMenuItems: MenuItem[] = [];
  filterFieldsArray: FilterField[] = [];
  isReady: boolean = false;
  attributeData: AttributeData;
  data: unknown;
  rowItems: MenuItem[] | undefined;
  bulkSelectionsItems: MenuItem[] | undefined;
  currentRow: Record<string, unknown>;
  visiblePrintLabel: boolean = false;
  showBulkUpdateDialog: boolean = false;
  visible: boolean = false;
  bulkUpdateHeader: string;
  reviewHeader: string;
  btnUpdate: { label: string; icon: string; command: () => void };
  entityCreatedAtAttributeId: string;
  regionGeofences: Array<{ id: string; name: string }> = [];
  zoneIds: string[] = [];
  isEntity = false;
  showAddDialog: boolean = false;
  deliveryStatusAttributeId: string;
  defaultAddressAttributeId: string;
  siteDeliveryAddressRelation: { id: string; name: string; ownerAttributeId?: string };
  deliveryAddressId: string;
  preventDeactivation: boolean;
  headerLabel: string;

  constructor(private siteDeliveryService: EntityService,
    private cs: CommonBindingDataService, private confirmationService: ConfirmationService,
    private router: Router, private messageService: MessageService, private actRoute: ActivatedRoute,
    private metaDataService: MetaDataService,
    private restApiService: RestApiService,
    private configService: ConfigService, private permissionAccessService: PermissionAccessService,
    private cd: ChangeDetectorRef, private loaderService: LoaderService,
    // private manageZonesService: ManageZonesService
  ) { }

  ngOnInit() {
    if (this.isActive) {
      this.initializeLabels();
      this.initializeRowItems();
      this.initializeModuleHeaderSecondaryBtnMenuItems();
      this.initializeOtherVariablesAndAPI();
      this.getMetaData();
      this.entityData.defaultSortColumn = this.defaultAddressAttributeId;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isActive && changes.isActive.currentValue === true && !this.data) {
      this.initializeLabels();
      this.initializeRowItems();
      this.initializeModuleHeaderSecondaryBtnMenuItems();
      this.initializeOtherVariablesAndAPI();
      this.getMetaData();
      this.entityData.defaultSortColumn = this.defaultAddressAttributeId;
    }
  }

  getMetaData() {
    // this.getEntityCount();
    const payload = this.getMultiApiPayload();
    this.metaDataService.metaDataMultipleApi(payload).subscribe((res) => {

      const sort = JSON.parse(res.sort.responseBody);
      const entityCreatedAt = sort.tabs[0].groups[0].fields.find(field => field.attributeCode === AppSettings.ENTITY_CREATED_AT);
      const defaultAddress =  sort.tabs[0].groups[0].fields.find(field => field.attributeCode === AppSettings.DEFAULT_ADDRESS);
      this.defaultAddressAttributeId = defaultAddress.attributeId;
      this.entityCreatedAtAttributeId = entityCreatedAt.attributeId;
      if (this.entityCreatedAtAttributeId) {
        // this.entityData.defaultSortColumn = this.entityCreatedAtAttributeId;
        this.entityData.defaultSortType = AppSettings.DESC_SORT_ORDER;
      }
      if(this.defaultAddressAttributeId) {
        this.entityData.defaultSortColumn = this.defaultAddressAttributeId;
      }
      const defaultTableView = JSON.parse(res.defaultTableView.responseBody);
      if (defaultTableView) {
        this.setVehicleTableView(defaultTableView);
      }
      this.cs.applyFilterToColumn(defaultTableView, sort);

    });
  }

  private getMultiApiPayload() {
    return [
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_SORT_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.SORT,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_TABLE_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.DEFAULT_TABLE_VIEW,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
    ]
  }

  onAdd() {
    this.headerLabel = this.cs.getLabel('site.lbl_add_delivery_address');
    this.deliveryAddressId = '';
    this.openAddDialog();
  }

  private initializeRowItems() {
    const isEdit = this.permissionAccessService.hasAccess('SITE_DELIVERY_DETAILS', '', 'EDIT', '');
    const isActivate = this.permissionAccessService.hasAccess('SITE_DELIVERY_DETAILS', '', 'ACTIVATE', '');
    const isMarkAsPrimary = this.permissionAccessService.hasAccess('SITE_DELIVERY_DETAILS', '', 'MARK_PRIMARY', '');
    this.rowItems = [
      ...(isEdit ? [{
        label: this.cs.getLabel('site.lbl_edit'),
        icon: `${AppIcons.EDIT_2} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'edit',
        command: () => {
          this.update();
        }
      }] : []),
      ...(isMarkAsPrimary ? [{
        label: this.cs.getLabel('site_delivery_address.lbl_mark_as_primary'),
        icon: `${AppIcons.PRIMARY_OUTLINE} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'markAsPrimary',
        command: () => {
          this.onUpdateStatus('Yes');
        }
      }] : []),
      { separator: true },

      ...(isActivate ? [{
        label: this.cs.getLabel('site_delivery_address.lbl_activate'),
        icon: `${AppIcons.CIRCLE_CHECKED} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'Active',
        command: () => {
          this.onUpdateStatus('Active');
        }
      }] : []),
      ...(isActivate ? [{
        label: this.cs.getLabel('site_delivery_address.lbl_deactivate'),
        icon: `${AppIcons.ACTIVATE} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'Inactive',
        command: () => {
          // this.onUpdateStatus('Inactive', this.cs.getLabel('site_delivery_address.lbl_deactivate'));
          if (this.preventDeactivation) {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.cs.getLabel('product.lbl_deactivate_address_error_msg')
            });
          } else {
            this.onUpdateStatus('Inactive');
          }
        }
      }] : []),
    ];
  }


  onUpdateStatus(status) {
    const isActive = status === 'Active';
    const isDefaultAddress = status === 'Yes';

    this.confirmationService.confirm({
      header: this.cs.getLabel(isDefaultAddress ? 'site_delivery_address.lbl_mark_as_primary_confirmation' : (isActive ? 'site_delivery_address.lbl_activate_confirmation' : 'site_delivery_address.lbl_deactivate_confirmation')),
      message: this.cs.getLabel(isDefaultAddress ? 'site_delivery_address.msg_mark_as_primary_confirmation' : (isActive ? 'site_delivery_address.msg_activate_confirmation' : 'site_delivery_address.msg_deactivate_confirmation')),
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'bg-white text-color cancel',
      acceptButtonStyleClass: 'bg-red-500 delete',
      acceptLabel: this.cs.getLabel(isDefaultAddress ? 'site_delivery_address.lbl_mark_as_primary' : (isActive ? 'site_delivery_address.lbl_activate' : 'site_delivery_address.lbl_deactivate')),
      rejectLabel: this.cs.getLabel('lbl_cancel'),
      accept: () => {
        const productData = {
          forTenantCode: this.configService.getForTenantCode(),
          entityCode: this.moduleName,
          countryCode: this.country[0].countryCode,
          languageCode: this.language?.[0]?.langCode || 'en',
          data: [{
            attributeId: isDefaultAddress ? this.defaultAddressAttributeId : this.deliveryStatusAttributeId,
            attributeValue: status
          }],
          relationshipData: [],
          viewCode: isDefaultAddress ? AppSettings.VIEW_CODE.ADD_EDIT_VIEW : AppSettings.VIEW_CODE.STATUS_UPDATE_VIEW,
        };

        this.siteDeliveryService.saveAttributeData(productData.entityCode, this.currentRow.id as string, productData).subscribe({
          next: () => {
            let successLabel = 'site_delivery_address.lbl_status_updated_successfully';
            if (isDefaultAddress) {
              successLabel = 'site_delivery_address.lbl_assigned_primary_successfully';
              this.entityData.defaultSortColumn =  this.defaultAddressAttributeId;
              this.entityData.defaultSortType = AppSettings.DESC_SORT_ORDER; 
              
            } else if (status === 'Active') {
              successLabel = 'site_delivery_address.lbl_activate_successfully';
            } else if (status === 'Inactive') {
              successLabel = 'site_delivery_address.lbl_deactivate_successfully';
            } else {
              successLabel = 'site_delivery_address.lbl_status_updated_successfully';
            }
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: this.cs.getLabel(successLabel),
            });
            setTimeout(() => {
              this.searchEntity();
              this.getEntityCount();
            }, 300);
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.cs.getLabel('site_delivery_address.lbl_failed_status')
            });
          }
        });
      },
      reject: () => {
      }
    });
  }


  onUpdateActionStatus() {
  }

  handleBulkUpdate() {
  }

  handleBulkPrint() {
  }

  private initializeModuleHeaderSecondaryBtnMenuItems() {

  }

  downloadImportTemplate() {
    this.siteDeliveryService.downloadImportTemplate(AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS).subscribe(result => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(result);
      a.download = 'Import Template.xlsx';
      a.click();
    });
  }

  downloadImportGuide() {
    this.siteDeliveryService.downloadImportGuide(AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS).subscribe(result => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(result);
      a.download = 'Import Guide.xlsx';
      a.click();
    });
  }

  private initializeLabels() {
    this.entityData.forTenantCode = this.configService.getForTenantCode();
    this.routePath = [{ label: "Sites", routerLink: 'app/sites' }];
  }

  private initializeOtherVariablesAndAPI() {
    this.btnLabel = this.cs.getLabel('Add Delivery Address');
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.deleteSiteMsg = this.cs.getLabel('site.message.confirm_delete_msg');
    this.deleteSiteHeaderMsg = this.cs.getLabel('site.message.confirm_delete_header');
  }



  public onChange(event) {
    for (const file of event.target.files) {
      if (file) {
        if (file.size > AppSettings.FILE_UPLOAD_MAX_SIZE_IN_BYTE) {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: this.cs.getLabel('err_file_size_exceed_msg') });
        } else {
          const formData = new FormData();
          formData.append("file", file);
          formData.append('forTenantCode', this.configService.getForTenantCode());
          formData.append('entityCode', AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS);
          formData.append('viewCode', AppSettings.VIEW_CODE.EXCEL_IMPORT_VIEW);
          this.cs.importEntity(formData, AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS).subscribe(() => {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('lbl_file_uploaded_successfully') });
          })
        }
      }
    }
  }

  getAttributeData() {
    this.siteDeliveryService.getAttributeDefinition(AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS, AppSettings.VIEW_CODE.BULK_UPDATE_VIEW).subscribe(res => {
      if (res) {
        this.setVehicleView(res);
      }
    });
  }

  setVehicleView(res) {
    this.attributeData = res;
    this.isLoading = false;
    this.filterFieldsArray = res.tabs[0].groups[0].fields;
    this.filterFieldsArray.forEach(element => {
      switch (element.attributeCode) {
        case 'vehicle_type':
          element.attributeValue = 'Type';
          element.isDisable = false;
          break;
        case 'vehicle_status':
          element.attributeValue = 'Status';
          element.isDisable = false;
          break;
      }
    });

  }

  setCurrentRowData(event) {
    this.currentRow = event;
    const status = Object.entries(event?.delivery_status)[0][0];
    const deliveryKey = Object.entries(event?.default_address)[0][0];
    this.updateRowItemStates(status, deliveryKey);
  }

  updateRowItemStates(status: string, isPrimary: string) {
    this.preventDeactivation = false;
    this.rowItems.forEach(rowItem => {
      switch (rowItem.value) {
        case 'Active':
          rowItem.disabled = status === 'Active';
          break;

        case 'Inactive':
          rowItem.disabled = status === 'Inactive';
          break;

        case 'markAsPrimary':
          rowItem.disabled = isPrimary === 'Yes';
          if (status === 'Inactive' && isPrimary === 'No') {
            rowItem.disabled = true;
          }
          break;

        default:
          break;
      }
    });
    if (status === 'Active' && isPrimary === 'Yes') {
      this.preventDeactivation = true;
    }
  }

  update() {
    this.headerLabel = this.cs.getLabel('site.lbl_update_delivery_address');
    this.onUpdateVehicle(this.currentRow.id);
  }

  getTableView() {
    this.siteDeliveryService.getAttributeDefinition(AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS, AppSettings.VIEW_CODE.DEFAULT_TABLE_VIEW, false).subscribe(res => {
      if (res) {
        this.setVehicleTableView(res);
      }
    });
  }

  setVehicleTableView(res: unknown) {
    this.isLoading = false;
    this.data = res;
    this.siteDeliveryAddressRelation = _.find((res as any)?.relations, { otherEntityCode: 'site', relation: "oneToOne" });
    this.attributeLabels = this.cs.getAttributeLabels(this.data) as unknown as Record<string, string>;
    this.deliveryStatusAttributeId = (res as any).tabs[0].groups[0].fields.find((field: Field) => field.attributeCode === 'delivery_status')?.attributeId;
    this.defaultAddressAttributeId = (res as any).tabs[0].groups[0].fields.find((field: Field) => field.attributeCode === 'default_address')?.attributeId;
    this.allColumns = this.cs.getColumns(this.data) as unknown as ColumnDefinition[];
    this.allColumns = (this.cs.getColumns(this.data) as unknown as ColumnDefinition[]).filter(column =>
      !['street', 'city', 'state', 'zip', 'delivery_time_from', 'delivery_time_to', 'address_line_2', 'address_short_name'].includes(column.attributeCode)
    );
    this.searchEntity();
    this.allColumns?.forEach((key, index) => {
      this.allColumns[index] = key;
      this.allColumns[index].field = key.attributeCode;
      this.allColumns[index].header = this.cs.getLabelValue(
        `site_delivery_address.fields.${key.attributeCode}.label`,
        this.attributeLabels,
        key.attributeCode
      );
      if (key.attributeCode === 'address_line_1') {
        this.allColumns[index].header = this.cs.getLabel('site.lbl_delivery_address');
      }
      if (key.attributeCode === 'default_address') {
        this.allColumns[index].header = this.cs.getLabel('site.lbl_default_address');
      }
      if (key.attributeCode === 'delivery_days') {
        this.allColumns[index].header = this.cs.getLabel('site.lbl_delivery_days');
      }
    });
    this.filterAvailableColumns = _.clone(this.allColumns);
  }


  getFilterView() {
    this.siteDeliveryService.getAttributeDefinition(AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS, AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW).subscribe(filterResponse => {
      if (filterResponse) {
        this.setVehicleFilterView(filterResponse);
      }
    });
  }

  setVehicleFilterView(filterResponse: unknown) {
    this.filterAttributeLabels = this.cs.getAttributeLabels(filterResponse) as unknown as Record<string, string>;
    this.filterList = this.cs.getColumns(filterResponse) as unknown as FilterField[];
    this.filterList = _.sortBy(this.filterList, 'attributeIndex');
  }

  getData() {
    return forkJoin({
      tableViewData: this.getTableView(),
      filterViewDate: this.getFilterView(),
    })
  }

  onSortData(sortColumnDetails) {
    if(sortColumnDetails?.attributeCode === 'default_address') {
      this.lastSortColumnDetails = sortColumnDetails?.attributeId;
    }
    this.entityData.defaultSortColumn = sortColumnDetails.attributeId;
    this.entityData.defaultSortType = (sortColumnDetails.sortOrder === 1) ? AppSettings.ASC_SORT_ORDER : AppSettings.DESC_SORT_ORDER;
    this.searchEntity();
  }

  searchEntity() {
    try {
      this.loaderService.show();
      this.entityData.countryCode = AppSettings.HEADER_COUNTRY_CODE;
      this.entityData.filters = [];
      this.entityData.filters.push({
        attributeId: this.siteDeliveryAddressRelation.ownerAttributeId as string,
        attributeValue: this.siteEntityId
      });
      this.tableData = [];
      this.siteDeliveryService.searchEntity(AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS, this.entityData, false)
        .subscribe({
          next: (res: ListResponse) => {
            if (!res?.data?.length) {
              this.tableData = [];
              this.totalRecords = 0;
              return;
            }
            this.listColumns = (res.data[0]?.values as unknown as ColumnDefinition[]) || [];
            this.pagination = res.pagination;
            this.totalRecords = res.count || 0;
            this.tableData = res.data.map(data => ({
              id: data.id,
              ...(data.values || {})
            }));
            this.isSiteReady = true;
          },
          error: () => {
            this.tableData = [];
            this.totalRecords = 0;
          },
          complete: () => {
            this.loaderService.hide();
          }
        });
    } catch {
      this.loaderService.hide();
      this.tableData = [];
      this.totalRecords = 0;
    }
  }

  onAddVehicle() {
    this.headerLabel = this.cs.getLabel('site.lbl_add_delivery_address');
    this.showAddDialog = true;
  }

  getLabel(params) {
    return `${params}hi`;
  }

  onRowClicked() {
    // Handle row click event
  }

  onActionBtnClicked() {
    // Handle action button click event
  }

  onPageChange(event) {
    this.entityData.offset = event?.first;
    this.entityData.limit = event?.rows;
    this.searchEntity();
  }

  itemPerPageChange(event) {
    this.itemsPerPage = event;
    this.entityData.limit = this.itemsPerPage;
    this.searchEntity();
  }

  onConfirmDelete() {
  }


  onVehicleDelete(event) {
    const country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      countryCode: country[0].countryCode,
      entityCode: AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS,
      entityIds: [
        event
      ]
    };
    this.siteDeliveryService.deleteEntityActionStatus(requestBody).subscribe({
      next: () => {
        this.cs.onDisplayMessage(this.data, 'vehicle', AppSettings.MSG_ACTION.DELETE, 'success');
        setTimeout(() => {
          this.searchEntity();
          // this.getEntityCount();
        }, 300);
      },
      error: () => {
        this.cs.onDisplayMessage(this.data, 'vehicle', AppSettings.MSG_ACTION.DELETE, 'error');
      }
    })
  }

  onUpdateVehicle(deliveryAddressId) {
    this.deliveryAddressId = deliveryAddressId;
    this.showAddDialog = true;
  }


  onRowSelect(event) {
    if (event.type === 'row') {
      this.router.navigate([`app/sites/site-details/delivery-details/${event.data.id}`]);
      this.siteDeliveryService.entityCurrentRow.next(event.data);
    }
  }


  onFilterValueChange(event) {
    const vData = event;
    this.entityData.filters = [];
    for (const [key, value] of Object.entries(vData)) {
      const attributeValue = value;
      if (attributeValue) {
        this.entityData.filters.push({
          attributeCode: key,
          attributeValue
        });
      }
    }
    this.entityData.filters = _.filter(_.uniq(this.entityData.filters, (item) => {
      if (item.attributeValue.length > 0) {
        return item.attributeCode;
      }
    }), (element) => {
      if (element.attributeValue.length > 0) {
        return true;
      }
      return false;
    });
    this.entityData.offset = event.first;
    this.entityData.filters = this.cs.mapAttributeIds(this.entityData.filters, this.filterList, 'filter');
    this.searchEntity();
  }


  onSearchValueChanges(event) {
    this.entityData.searchStr = event;
    this.searchEntity();
  }

  getEntityCount() {
    const country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    const entityCountPayload = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS,
      countryCode: <string>country[0].countryCode,
    };


    this.siteDeliveryService.getEntityCount(AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS, entityCountPayload, false).subscribe((res: EntityCountsResponse) => {
      if (res?.counts) {
        res.counts.ALL = res.counts.ALL - res.counts.DELETED;
      }
      this.entityCount = res;
    })
  }

  tabViewChange(event) {
    this.activeIndex = event.index;
    const actionStatus = '';
    switch (event.index) {
      case 0:
        this.entityData.actionStatus = actionStatus;
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        this.tableRef.clearFilter();
        this.searchEntity();
        break;
      case 1:
        this.entityData.actionStatus = AppSettings.VEHICLE_TAB_LIST.ASSIGNED;
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        this.tableRef.clearFilter();
        this.searchEntity();
        break;

      case 2:
        this.entityData.actionStatus = AppSettings.VEHICLE_TAB_LIST.UNASSIGNED;
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        this.tableRef.clearFilter();
        this.searchEntity();
        break;

      case 3:
        this.entityData.actionStatus = AppSettings.VEHICLE_TAB_LIST.DELETE
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_DELETED;
        this.entityData.filters = [];
        this.tableRef.clearFilter();
        this.searchEntity();
        break;

      default:
        break;
    }
    if (event.index === 0 || event.index === 1 || event.index === 2) {
      this.showBulkUpdateDialog = false;
    }
  }

  onBulkDataUpdateEvent() {
    this.searchEntity();
  }

  onBulkDataDeleteEvent(event) {
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS,
      countryCode: this.country[0].countryCode,
      entityIds: event
    };
    this.siteDeliveryService.deleteEntityActionStatus(requestBody).subscribe({
      next: () => {
        this.cs.onDisplayMessage(this.data, 'vehicle', AppSettings.MSG_ACTION.BULK_DELETE, 'success');
        this.searchEntity();
        // this.getEntityCount();
      },
      error: () => {
        this.cs.onDisplayMessage(this.data, 'vehicle', AppSettings.MSG_ACTION.BULK_DELETE, 'error');
      }
    });
  }

  openAddDialog() {
    if (this.isSiteReady) {
      this.showAddDialog = true;
    }
  }

  onSaveSuccess() {
    this.showAddDialog = false;
    this.searchEntity();
    this.saveSuccess.emit();
  }
}

